import React from 'react';

const VeraRubin = () => (
  <article className="news-article-body">
    <p className="news-lead">
      Announced at CES 2026 and shipping to hyperscalers in H2 2026, NVIDIA's
      <strong> Vera Rubin</strong> platform redefines what a rack-scale AI supercomputer can
      deliver. Built on TSMC 3nm dual-die GPUs with HBM4 memory, a single NVL72 rack delivers
      <strong> 3.6 exaflops</strong> of inference compute — 5× the performance of Blackwell
      at one-tenth the cost per token.
    </p>

    <h2>Named After a Trailblazer</h2>
    <p>
      The platform is named after <strong>Vera Rubin</strong> (1928–2016), the American
      astronomer whose observations provided the first strong evidence for dark matter in
      galaxies. NVIDIA has a tradition of naming its GPU architectures after pioneering
      scientists — Hopper, Ampere, Blackwell — and Rubin continues that lineage with a
      nod to one of the 20th century's most consequential astronomers.
    </p>

    <h2>The Rubin GPU — Raw Specifications</h2>
    <div className="news-specs-grid">
      <div className="news-spec-item">
        <span className="news-spec-label">Process Node</span>
        <span className="news-spec-value">TSMC 3nm dual-die</span>
      </div>
      <div className="news-spec-item">
        <span className="news-spec-label">Transistors</span>
        <span className="news-spec-value">336 billion</span>
      </div>
      <div className="news-spec-item">
        <span className="news-spec-label">Memory</span>
        <span className="news-spec-value">288 GB HBM4</span>
      </div>
      <div className="news-spec-item">
        <span className="news-spec-label">Memory Bandwidth</span>
        <span className="news-spec-value">22 TB/s</span>
      </div>
      <div className="news-spec-item">
        <span className="news-spec-label">Inference (NVFP4)</span>
        <span className="news-spec-value">50 PFLOPS — 5× Blackwell</span>
      </div>
      <div className="news-spec-item">
        <span className="news-spec-label">Training (NVFP4)</span>
        <span className="news-spec-value">35 PFLOPS — 3.5× Blackwell</span>
      </div>
      <div className="news-spec-item">
        <span className="news-spec-label">Power (Max Q / Max P)</span>
        <span className="news-spec-value">~1.8 kW / ~2.3 kW</span>
      </div>
    </div>

    <h2>The NVL72 Rack — Rack-Scale Architecture</h2>
    <p>
      The <strong>Vera Rubin NVL72</strong> is the rack-scale deployment unit: 72 Rubin GPUs
      paired with 36 Vera CPUs (NVIDIA's in-house ARM-based CPU, replacing AMD EPYC in the
      DGX lineage). The entire rack functions as a single logical compute unit via NVLink
      fabric — no PCIe bottleneck between GPUs.
    </p>
    <div className="news-specs-grid">
      <div className="news-spec-item">
        <span className="news-spec-label">GPUs per Rack</span>
        <span className="news-spec-value">72 Rubin GPUs</span>
      </div>
      <div className="news-spec-item">
        <span className="news-spec-label">CPUs per Rack</span>
        <span className="news-spec-value">36 Vera CPUs</span>
      </div>
      <div className="news-spec-item">
        <span className="news-spec-label">Rack Inference</span>
        <span className="news-spec-value">3.6 EFLOPS (NVFP4)</span>
      </div>
      <div className="news-spec-item">
        <span className="news-spec-label">Rack Training</span>
        <span className="news-spec-value">2.5 EFLOPS</span>
      </div>
      <div className="news-spec-item">
        <span className="news-spec-label">Full POD (40 racks)</span>
        <span className="news-spec-value">1,152 GPUs · 60 EFLOPS</span>
      </div>
      <div className="news-spec-item">
        <span className="news-spec-label">Cost per Token</span>
        <span className="news-spec-value">10× cheaper than Blackwell</span>
      </div>
    </div>

    <h2>The "Data Centre as a Computer" Philosophy</h2>
    <p>
      Vera Rubin is the fullest expression of NVIDIA's architectural thesis: the entire
      data centre is a single computer. The platform integrates:
    </p>
    <ul>
      <li><strong>NVLink-C2C</strong>: On-package CPU-GPU interconnect (eliminates PCIe latency)</li>
      <li><strong>NVSwitch</strong>: All-to-all GPU communication within a rack at 900 GB/s per GPU</li>
      <li><strong>Quantum InfiniBand / Spectrum Ethernet</strong>: Scale-out networking between racks at 400 Gb/s+</li>
      <li><strong>NVIDIA Blackwell storage architecture</strong>: Tiered storage co-designed for AI checkpoint and inference cache workloads</li>
    </ul>
    <p>
      The result: a 40-rack POD with 1,152 GPUs behaves like a single machine from the
      software perspective. Distributed training frameworks need no special topology-awareness.
    </p>

    <h2>Who's Deploying It?</h2>
    <p>
      AWS, Google Cloud, Microsoft Azure, Oracle Cloud Infrastructure, and CoreWeave have
      all confirmed Vera Rubin deployments in H2 2026. The platform is also the basis for
      NVIDIA's next-generation DGX SuperPOD — the reference architecture for AI factories
      that NVIDIA sells to enterprises and national labs building sovereign AI infrastructure.
    </p>

    <h2>Designed for Agentic AI</h2>
    <p>
      The 10× cost-per-token reduction versus Blackwell is not a coincidence. NVIDIA specifically
      designed Vera Rubin for the emerging era of <strong>agentic AI</strong> — where models
      run continuously, spawn sub-agents, and execute multi-step tasks autonomously. Agentic
      workloads generate far more inference tokens than conversational AI, demanding massive
      throughput at low cost. Vera Rubin's MoE-optimised architecture (¼ the GPUs needed for
      MoE model training vs Blackwell) directly addresses this workload profile.
    </p>

    <div className="news-insight-box">
      <h3>Key Takeaway</h3>
      <p>
        Vera Rubin is not just a faster GPU — it is a rearchitecting of how AI compute is
        packaged and priced. The 10× cost-per-token reduction enables workload categories
        that are currently economically infeasible (continuous agentic pipelines, long-context
        batch reasoning, real-time multi-modal inference) to become standard deployments.
        The question for every enterprise AI team shifts from "can we afford to run this?"
        to "what would we do if inference were effectively free?"
      </p>
    </div>
  </article>
);

export default VeraRubin;
